import { FC } from 'react';

import { CouponSettlementLineRow } from '@shared/api/couponSettlement/couponSettlement.interface';
import { GiftSettlementMethod } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import {
  amountColorClass,
  couponCodeLabel,
  formatFee,
  formatSettlementAmount,
  won,
} from '../../utils/coupon-settlement.utils';
import { formatDate } from '../../utils/delivery.utils';

export interface CouponSettlementLineTableProps {
  /** 정산서 상세 행 (서버가 발송 asc → 환불 asc로 정렬해 내려줌) — 페이지 슬라이스 가능 */
  rows: CouponSettlementLineRow[];
  totalAmount: number;
  /** 서버 계산 수수료 합계 — FeeRate만, Rebate는 null('-') */
  totalFee: number | null;
  /** 수수료율(%) — 헤더 표기용 */
  feeRate?: number;
  /** 정산 방식 — FeeRate일 때만 요율 표시 */
  method?: GiftSettlementMethod;
  /** 순번 시작 오프셋 (페이지네이션 시 (page−1)×PAGE_SIZE) */
  startIndex?: number;
  /** 합계 행의 전체 라인 수 — rows가 검색/페이지 슬라이스여도 문서 전체 기준으로 표기 */
  totalCount?: number;
  /** 지정 시 행마다 "제외" 버튼 노출 (Draft·Admin 편집 모드) */
  onRemoveRow?: (row: CouponSettlementLineRow) => void;
  /** 제외 처리 중인 쿠폰 id — 해당 행 버튼만 로딩 표시, 나머지는 비활성 */
  removingCouponId?: string | null;
  /** 재발행(isAdditional) 뱃지 표시 — 현재 모든 view에서 켬 (2026-07-02 "admin과 동일 view" 결정) */
  showAdditionalBadge?: boolean;
}

/**
 * 쿠폰 정산서의 라인을 테이블로 보여준다 (상세 모달용).
 * 컬럼 구성은 미정산 상세보기(발송 테이블)와 동일 — isRefund 구분 배지로 발송(+초록)/
 * 환불(−빨강)을 나눈다. 모든 값은 `:id/detail` 응답의 서버 확정값 그대로이며
 * (폴백·수수료 계산 없음), 정렬도 서버 순서를 따른다.
 */
export const CouponSettlementLineTable: FC<CouponSettlementLineTableProps> = ({
  rows,
  totalAmount,
  totalFee,
  feeRate,
  method,
  startIndex = 0,
  totalCount,
  onRemoveRow,
  removingCouponId,
  showAdditionalBadge = false,
}) => {
  if (rows.length === 0) {
    return <div className="p-8 text-center text-gray-500">정산 내역이 없습니다.</div>;
  }

  return (
    <div className="overflow-x-auto rounded-lg bg-white shadow">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500">
          <tr>
            <th className="px-3 py-2 text-center">순번</th>
            <th className="px-3 py-2 text-left">구분</th>
            <th className="px-3 py-2 text-left">쿠폰번호</th>
            <th className="px-3 py-2 text-left">구매상품명</th>
            <th className="px-3 py-2 text-left">
              <p>구매자</p>
              <p className="mt-0.5">연락처</p>
            </th>
            <th className="px-3 py-2 text-left">
              <p>고인명</p>
              <p className="mt-0.5">발인일</p>
            </th>
            <th className="px-3 py-2 text-left">
              <p>구매일/발송일</p>
              <p className="mt-0.5">환불일</p>
            </th>
            <th className="px-3 py-2 text-right">판매가</th>
            <th className="px-3 py-2 text-left">
              <p>수령상품</p>
              <p className="mt-0.5">정상가</p>
            </th>
            <th className="px-3 py-2 text-right">도매가</th>
            <th className="px-3 py-2 text-right">
              수수료
              {method !== GiftSettlementMethod.Rebate && feeRate != null && (
                <span className="font-normal text-gray-400"> ({feeRate}%)</span>
              )}
            </th>
            <th className="px-3 py-2 text-right">정산금</th>
            <th className="px-3 py-2 text-left">
              <p>소속</p>
              <p className="mt-0.5">지도사명</p>
            </th>
            <th className="px-3 py-2 text-left">지도사연락처</th>
            {onRemoveRow && <th className="px-3 py-2 text-center">관리</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, idx) => (
            <tr
              key={`${row.giftCouponId}-${row.isRefund}-${!!row.isRenewal}`}
              className="hover:bg-gray-50"
            >
              <td className="whitespace-nowrap px-3 py-3 text-center text-xs tabular-nums text-gray-500">
                {startIndex + idx + 1}
              </td>
              <td className="px-3 py-3">
                <span
                  className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
                    row.isRefund
                      ? 'bg-rose-50 text-rose-700'
                      : 'bg-emerald-50 text-emerald-700'
                  }`}
                >
                  {row.isRefund ? (row.isRenewal ? '연장 차감' : '환불 차감') : '발송 수익금'}
                </span>
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-xs font-medium text-gray-700">
                <p>{couponCodeLabel(row.fullCode)}</p>
                {showAdditionalBadge && row.isAdditional && (
                  <span className="mt-0.5 inline-block rounded bg-sky-100 px-1 py-0.5 text-[10px] font-medium text-sky-700">
                    재발행
                  </span>
                )}
              </td>
              <td className="px-3 py-3">
                <p className="break-words text-xs font-medium text-gray-700">
                  {row.purchaseItemName || '-'}
                </p>
              </td>
              <td className="px-3 py-3 text-xs">
                <p className="font-bold text-gray-700">{row.buyerName || '-'}</p>
                {row.buyerPhoneNumber && (
                  <p className="mt-0.5 text-gray-400">
                    {phoneNumberStringFormator(row.buyerPhoneNumber)}
                  </p>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                <p>{row.deceasedName || '-'}</p>
                <p className="mt-0.5 font-bold text-gray-700">
                  {row.coffinOutDate ? formatDate(row.coffinOutDate) : '-'}
                </p>
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                <p>{formatDate(row.purchasedAt)}</p>
                {/* 발송일은 환불 라인에도 표시 — 발송된 건에 대한 환불이라는 맥락 확인용
                    (미발송 환불이거나 백엔드 미반영이면 '-') */}
                <p className="mt-0.5 font-bold text-gray-700">
                  {row.deliveredAt ? formatDate(row.deliveredAt) : '-'}
                </p>
                {row.isRefund && (
                  <p className="mt-0.5 font-bold text-rose-700">
                    {row.isRenewal ? '연장' : '환불'} {formatDate(row.refundedAt)}
                  </p>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-700">
                {won(row.priceRetail)}
              </td>
              <td className="px-3 py-3 text-xs text-gray-600">
                {/* 수령상품명은 길어서 말줄임 — 전체 이름은 hover 툴팁으로 */}
                <p
                  className="max-w-[160px] truncate"
                  title={row.receiveItemName || undefined}
                >
                  {row.receiveItemName || '-'}
                </p>
                <p className="mt-0.5 tabular-nums text-gray-500">
                  {won(row.priceRegular)}
                </p>
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                {won(row.priceWhole)}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                {row.fee != null ? formatFee(row.fee) : '-'}
              </td>
              <td
                className={`whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums ${
                  row.isRefund ? 'text-rose-700' : 'text-emerald-700'
                }`}
              >
                {row.amount.toLocaleString('ko-KR')}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                <p>{row.teamName || '-'}</p>
                <p className="mt-0.5 font-bold text-gray-700">{row.workerName || '-'}</p>
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                {row.workerPhoneNumber
                  ? phoneNumberStringFormator(row.workerPhoneNumber)
                  : '-'}
              </td>
              {onRemoveRow && (
                <td className="whitespace-nowrap px-3 py-3 text-center">
                  <button
                    onClick={() => onRemoveRow(row)}
                    disabled={!!removingCouponId}
                    className="rounded border border-rose-300 px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {removingCouponId === row.giftCouponId ? '제외 중...' : '제외'}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50 text-sm font-semibold">
          <tr>
            <td className="px-3 py-3 text-gray-500" colSpan={10}>
              정산금 합계 ({(totalCount ?? rows.length).toLocaleString('ko-KR')}건)
            </td>
            <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
              {totalFee != null ? formatFee(totalFee) : '-'}
            </td>
            <td
              className={`whitespace-nowrap px-3 py-3 text-right tabular-nums ${amountColorClass(
                totalAmount,
              )}`}
            >
              {formatSettlementAmount(totalAmount)}
            </td>
            <td colSpan={onRemoveRow ? 3 : 2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
